import { TNetworks, TUsers } from 'integration/resources/newOpportunities'

export type TPriorityItem = {
  id: string
  type: string
  max_percentage: number
  percentage: number
  value: number
  diagnosis?: string
  stores_quantity?: number
}

export type TOpportunitiesV3PriorityOption = {
  id: string
  name: string
}

export type TOpportunitiesV3Competitor = {
  id: string
  name: string
}

export type TOpportunitiesV3Filters = {
  priorities: TOpportunitiesV3PriorityOption[]
  offers: string[]
  public_ratings: string[]
  fidelities: string[]
  segments: string[]
  networks: TNetworks[]
  users: TUsers[]
}

export type TOpportunitiesV3FiltersResponse = {
  priorities?: TOpportunitiesV3PriorityOption[]
  offers: string[]
  public_ratings: string[]
  fidelities: string[]
  segments: string[]
  networks: TNetworks[]
  users: TUsers[]
}

export type TOpportunitiesV3SummaryPriority = {
  id: number
  type: string
  max_percentage: number | null
  percentage: number | null
  value: number
  diagnosis?: string
  stores_quantity?: number
}

export type TOpportunitiesV3Summary = {
  year_month: string
  priorities: TOpportunitiesV3SummaryPriority[]
}

export type TOpportunitiesV3SummaryResponse = TOpportunitiesV3Summary & {
  status: number
  metadata: {
    requestId: string
  }
}

export type TOpportunitiesV3Priority = {
  order: number
  type: string
  value: number
}

export type TOpportunitiesV3PublicRating = {
  name: string
  bg_color: string
}

export type TOpportunitiesV3Description = {
  title: string
  name?: string
  value: number
}

export enum OPPORTUNITIES_V3_LIST_ITEM_VARIANT {
  OPPORTUNITY = 'opportunity',
  OVERDUE = 'overdue',
  APNEF = 'apnef',
  PARTNERSHIP = 'partnership'
}

type TOpportunitiesV3ListItemBase = {
  id: string
  opportunity_id: string
  type: TOpportunitiesV3Type
}

export type TOpportunitiesV3Item = {
  list_item_variant: OPPORTUNITIES_V3_LIST_ITEM_VARIANT.OPPORTUNITY
  store_name?: string
  tab_number: number
  store_cnpj?: string
  classification?: { title: string; icon?: number }
  public_rating: TOpportunitiesV3PublicRating
  priorities?: TOpportunitiesV3Priority[]
  description?: TOpportunitiesV3Description
} & TOpportunitiesV3ListItemBase

export type TOpportunitiesV3ListParams = {
  page?: number
  per_page?: number
  search?: string
  kind?: OPP_ENUM_FILTER
  priority?: string[]
  offer?: string[]
  public_rating?: string[]
  fidelity?: string[]
  segment?: string[]
  network?: string[]
  subsidiary?: string[]
  user_std_code?: string[]
  order_by?: string
  order_by_direction?: string
}

export enum OPP_ENUM_FILTER {
  OPPORTUNITY = 1,
  ACTIVE_BASE = 2,
  CHARGES = 3,
  APNEF = 4,
  PARTNER_IA = 5
}

export type TOpportunitiesV3ListApiPriority = {
  description: string
  value: number
  diagnosis?: string
  level: number
}

export type TOpportunitiesV3ListApiItem = {
  id: string
  classification?: { title: string; icon?: number }
  store: {
    tab_number: string
    cnpj?: string
    name_fantasy?: string
  }
  public_rating: string
  public_rating_bg_color: string
  metrics?: {
    potential_value?: number
  }
  priorities?: TOpportunitiesV3ListApiPriority[]
  type: number
}

export type TOpportunitiesV3OverdueContractListApiItem = TOpportunitiesV3OverdueContractItem & {
  opportunity_id: string
}

export type TOpportunitiesV3ApnefListApiItem = TOpportunitiesV3ApnefItem & {
  opportunity_id: string
}

export type TOpportunitiesV3PartnershipListApiItem = TOpportunitiesV3PartnershipItem & {
  opportunity_id: string
}

export type TOpportunitiesV3ListResponseData =
  | TOpportunitiesV3ListApiItem[]
  | {
      contracts?: TOpportunitiesV3OverdueContractListApiItem[]
      apnef?: TOpportunitiesV3ApnefListApiItem[]
      parceria_certa?: TOpportunitiesV3PartnershipListApiItem[]
    }

export type TOpportunitiesV3DetailsStore = {
  store_name?: string
  tab_number: string
  segment_name?: string
  subsegment_name?: string
  store_cnpj_number?: string
  logo_image_url?: string
}

export type TOpportunitiesV3DetailsPriority = {
  id: string
  type: string
  max_percentage: number
  percentage: number
  value: number
  diagnosis?: string
  stores_quantity?: number
}

export type TOpportunitiesV3DetailsRecommendation = {
  id: string
  title: string
  description: string
}

export type TOpportunitiesV3DetailsStayTuned = {
  id: string
  title: string
  icon: number
}

export enum INDICATOR_VALUE_TYPE_ENUM {
  CURRENCY = 0,
  PERCENTAGE = 1,
  NUMERIC = 2
}

export enum INDICATOR_TYPE_ENUM {
  PERCENTAGE = 0,
  PERCENTAGE_POINTS = 1,
  NUMERIC = 2
}

export type TOpportunitiesV3IndicatorItem = {
  title: string
  value: number
  value_type: INDICATOR_VALUE_TYPE_ENUM
  indicator?: number
  indicator_tip?: string
  indicator_type?: INDICATOR_TYPE_ENUM
  tip: string
  ref_date: string
}

export type TOpportunitiesV3IndicatorsSection = {
  title: string
  items: TOpportunitiesV3IndicatorItem[]
}

export type TOpportunitiesV3Indicators = {
  market_diagnosis: TOpportunitiesV3IndicatorsSection
  delinquency_diagnosis: TOpportunitiesV3IndicatorsSection
  productivity_summary: TOpportunitiesV3IndicatorsSection
  simulation_diagnosis: TOpportunitiesV3IndicatorsSection
  apnef_diagnosis: TOpportunitiesV3IndicatorsSection
}

export type TOpportunitiesV3TabData = {
  status?: number
  accreditation_date?: string
  last_contract_date?: string
  fantasy_name: string
  cnpj: string
  phone?: string
  website: string
  email: string
  address: string
  google_maps_link?: string
}

export type TOpportunitiesV3Details = {
  type: OPPORTUNITIES_TYPES
  store: TOpportunitiesV3DetailsStore
  potential_value?: number
  tab_data?: TOpportunitiesV3TabData
  has_unlink_request?: boolean
  evaluation_expired: boolean
  year_month: number
  priorities: TOpportunitiesV3DetailsPriority[]
  recommendations: TOpportunitiesV3DetailsRecommendation[]
  stay_tuned: TOpportunitiesV3DetailsStayTuned[]
  indicators?: TOpportunitiesV3Indicators
}

export type TOpportunitiesV3DetailsHeader = {
  store_name: string
  store_cnpj: string
  segment_name: string
  opportunity_potential_value: number
  reference_month: string
  user_name?: string
  tab_number?: string
}

export type TOpportunitiesV3DetailsActions = {
  can_exclude_opportunity: boolean
  can_evaluate: boolean
  evaluation_expired: boolean
}

export type TOpportunitiesV3DetailsRecommendations = {
  atendimento?: {
    title: string
    description?: string
  }[]
  fique_ligado?: {
    title: string
    icon: number
  }[]
}

export enum OPPORTUNITIES_TYPES {
  ODC = 1,
  ACTIVE_BASE = 2
}

export const OPPORTUNITY_STATUS_TYPE_ENUM = {
  ACTIVE: 1,
  INACTIVE: 2
}

export type TOpportunitiesV3DetailsResponse = {
  id: string
  type: OPPORTUNITIES_TYPES
  header: TOpportunitiesV3DetailsHeader
  actions: TOpportunitiesV3DetailsActions
  recommendations: TOpportunitiesV3DetailsRecommendations
  priorities?: {
    id: number
    description: string
    value: number
    max_percentage: number
    percentage: number
    diagnosis?: string
  }[]
  indicators?: TOpportunitiesV3Indicators
  store_data?: {
    status: number
    accreditation_date: string
    last_contract_date: string
    fantasy_name: string
    cnpj: string
    phone?: string
    website: string
    email: string
    address: string
    google_maps_link: string
  }
}

export enum OPPORTUNITY_TYPE_ENUM {
  ODC = 'odc',
  ACTIVE_BASE = 'active_base'
}

export type TOpportunitiesV3Type = OPPORTUNITY_TYPE_ENUM

export type TOpportunitiesV3SupportListParams = {
  opportunity_id: string
  page?: number
  per_page?: number
}

export type TOpportunitiesV3UnlinkRequestPayload = {
  opportunity_id: string
  justification: string
  attachments?: {
    file_name_presigned: string
    file_name: string
    file_url: string
    file_size: number
    submit_date: string
  }[]
}

export type TOpportunitiesV3EvaluationPriorityPayload = {
  score?: number | null
  agreement?: 'agree' | 'disagree' | null
  new_priority?: string | null
  comment?: string | null
}

export type TOpportunitiesV3EvaluationTabPayload = {
  fidelity?: number | null
  fee?: number | null
  approval?: number | null
}

export type TOpportunitiesV3EvaluationCompetitorPayload = {
  competitor_id?: string | null
  price?: number | null
  coupon?: 'yes' | 'no' | null
  entry?: 'yes' | 'no' | null
  commission_store?: number | null
  commission_sales?: number | null
  fair?: 'yes' | 'no' | null
  presence?: number | null
  less_bureaucracy?: 'yes' | 'no' | null
  comment?: string | null
}

export type TOpportunitiesV3EvaluationPayload = {
  opportunity_id: string
  priority?: TOpportunitiesV3EvaluationPriorityPayload
  tab?: TOpportunitiesV3EvaluationTabPayload
  competitors?: TOpportunitiesV3EvaluationCompetitorPayload[]
}

export type TOpportunitiesV3OverdueContractItem = {
  id: string
  store: {
    name_fantasy: string
    tab_number: number
    store_cnpj_number: number
    debit_balance: number
    overdue_days: number
  }
  contract_infos: {
    contract_number: number
    installment_quantity: number
    installment_value: number
    accounting_balance: number
    safra: number
    installment_quantity_delayed: number
    installment_first_delayed: number
    tracker: boolean
  }
  customer_infos: {
    customer_name: string
    customer_cpf_cnpj: string
    phone_number_01: string
    phone_number_02: string
    address_city_neighborhood: string
  }
  car_infos: {
    car_model: string
    car_chassis_number: string
    car_year_model: number
    car_license_plate: string
  }
}

export type TOpportunitiesV3OverdueContractListItem = TOpportunitiesV3OverdueContractItem &
  TOpportunitiesV3ListItemBase & {
    list_item_variant: OPPORTUNITIES_V3_LIST_ITEM_VARIANT.OVERDUE
  }

export type TOpportunitiesV3ApnefItem = {
  id: string
  store_tab_number?: string
  proposal_number: number
  customer_cpf_cnpj: string
  rating: number
  apnef_date?: string | null
  entry_percentage_value?: number | null
  financing_value?: number | null
  installment_value?: number | null
  installment_quantity?: number | null
  account_holder?: boolean
  car_brand?: string
  car_model?: string
  car_year?: number
  apnef_goal_impact?: string | null
  apnef_impact_value?: number | null
  created_at?: string
  updated_at?: string
  deleted_at?: string | null
}

export type TOpportunitiesV3ApnefListItem = TOpportunitiesV3ApnefItem &
  TOpportunitiesV3ListItemBase & {
    list_item_variant: OPPORTUNITIES_V3_LIST_ITEM_VARIANT.APNEF
  }

export type TOpportunitiesV3PartnershipItem = {
  id: string
  store_tab_number: number
  proposal_number: number
  fantasy_name: string
  customer_rating: string
  total_financing_value: number
  installment_value: number
  entry_percentage_value: number
  customer_cpf_cnpj: string
  model_year: number
  car_model: string
  account_holder: 'Sim' | 'Não'
  simulation_date: string
  processed_at: string
  car_brand: string
  created_at: string
  updated_at: string
  deleted_at: string | null
}

export type TOpportunitiesV3PartnershipListItem = TOpportunitiesV3PartnershipItem &
  TOpportunitiesV3ListItemBase & {
    list_item_variant: OPPORTUNITIES_V3_LIST_ITEM_VARIANT.PARTNERSHIP
  }

export type TOpportunitiesV3ListItem =
  | TOpportunitiesV3Item
  | TOpportunitiesV3OverdueContractListItem
  | TOpportunitiesV3ApnefListItem
  | TOpportunitiesV3PartnershipListItem
